// AppPromo.jsx
//
// Η ενότητα «σύντομα» της εφαρμογής, στην αρχική.
//
// Κρατάει τη γλώσσα του site: γκρι κλίμακα, τριχοειδή περιγράμματα, ίδια
// τυπογραφία. Το μόνο χρωματιστό πράγμα είναι η οθόνη του κινητού, που ανήκει
// στην εφαρμογή και όχι στο site.
//
// Η συσκευή μπαίνει ΑΡΙΣΤΕΡΑ, ανάποδα από το hero της αρχικής (κείμενο
// αριστερά, πάνελ δεξιά). Δύο ενότητες με την ίδια διάταξη στην ίδια σελίδα
// διαβάζονται σαν πρότυπο.

import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { FaApple, FaGooglePlay } from 'react-icons/fa6'
import { PhonePreview } from './appPreview.jsx'
import { useCountdown } from './appCountdown.js'

const POINTS = [
  {
    title: 'Μόνο τα δικά σου μαθήματα',
    body: 'Διαλέγεις πεδίο και βλέπεις τη δική σου σειρά εξετάσεων, με τα ειδικά αν δίνεις.',
  },
  {
    title: 'Ειδοποιήσεις πριν από κάθε εξέταση',
    body: 'Στις 100, 50, 30 και 7 μέρες, και το απόγευμα πριν από κάθε μάθημα.',
  },
  {
    title: 'Βάσεις και μόρια μαζί',
    body: 'Βάλε τα μόριά σου και δες μόνο τις σχολές που πιάνεις.',
  },
]

/** Εμφάνιση μία φορά, όταν η συσκευή μπει στο κάδρο. */
function useRevealOnce() {
  const ref = useRef(null)
  const [shown, setShown] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduced || typeof IntersectionObserver === 'undefined') {
      setShown(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShown(true)
          observer.disconnect()
        }
      },
      { threshold: 0.25 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return [ref, shown]
}

export default function AppPromo() {
  const { days, progress } = useCountdown()
  const [ref, shown] = useRevealOnce()

  return (
    <section
      ref={ref}
      aria-labelledby="app-promo-title"
      /* Λευκό: η επόμενη ενότητα είναι gray-50 και δύο γκρι ζώνες στη σειρά
         κολλάνε οπτικά. Το σκούρο κινητό ξεχωρίζει και περισσότερο σε λευκό. */
      className="border-t border-gray-100 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          {/* Η συσκευή. Πρώτη στο desktop, δεύτερη στο κινητό: σε στενή οθόνη
              το κείμενο πρέπει να εξηγήσει τι κοιτάει κανείς. */}
          <div className="order-2 lg:order-1 lg:col-span-5">
            <div
              className={[
                'mx-auto w-full max-w-[300px] transform-gpu transition duration-700 ease-out motion-reduce:transition-none',
                shown ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4',
              ].join(' ')}
            >
              <PhonePreview days={days} progress={progress} />
              <p className="mt-4 text-center text-xs text-gray-500">
                Ζωντανή μέτρηση. Η ημερομηνία είναι εκτίμηση μέχρι να βγει το ΦΕΚ.
              </p>
            </div>
          </div>

          {/* Το κείμενο. */}
          <div className="order-1 lg:order-2 lg:col-span-7">
            <p className="text-sm font-medium text-gray-500">Σύντομα</p>

            <h2
              id="app-promo-title"
              className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight leading-[1.15] text-gray-900"
            >
              Πόσο έμεινε, στο κινητό σου.
            </h2>

            <p className="mt-4 text-base sm:text-lg text-gray-600 max-w-xl">
              Ετοιμάζουμε το <span className="font-medium text-gray-900">Panellele</span>: σου λέει
              πόσο έμεινε για κάθε μάθημα που δίνεις και σου το θυμίζει πριν φτάσει.
            </p>

            {/* Λίστα με μία γραμμή ανάμεσα, όχι κάρτες με εικονίδια. Τρεις
                πανομοιότυπες κάρτες με εικονίδιο είναι η πιο τυποποιημένη
                διάταξη που υπάρχει, και η αρχική τη χρησιμοποιεί ήδη αλλού. */}
            <dl className="mt-8 divide-y divide-gray-100 border-y border-gray-100">
              {POINTS.map((point) => (
                <div key={point.title} className="py-4">
                  <dt className="text-sm font-medium text-gray-900">{point.title}</dt>
                  <dd className="mt-1 text-sm text-gray-600">{point.body}</dd>
                </div>
              ))}
            </dl>

            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3">
              <Link
                to="/app"
                className="cursor-pointer inline-flex items-center justify-center rounded-xl bg-gray-900 px-5 py-3 text-sm font-medium text-white hover:bg-gray-800 transition"
              >
                Δες τι θα κάνει
              </Link>

              <span className="inline-flex items-center gap-4 text-sm text-gray-500">
                <span className="inline-flex items-center gap-2">
                  <FaApple className="h-4 w-4" aria-hidden="true" />
                  iPhone
                </span>
                <span className="inline-flex items-center gap-2">
                  <FaGooglePlay className="h-3.5 w-3.5" aria-hidden="true" />
                  Android
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
